// Generic Chat Service — the chat core shared by the Studio (with a recipe)
// and the Lab Assistant (no recipe). Owns:
//   - Conversation history
//   - Intent classification
//   - Persona-aware response generation
//   - Modification-preflight (returns requiresConfirmation: true)
//
// Does NOT own:
//   - Modification execution (caller does this after user confirms)
//   - Coin spending (caller's responsibility)
//   - Persona welcome messages (caller picks)
//   - SessionStorage persistence (caller's responsibility)

import { RecipeFormData } from '@/types/recipe';
import {
    ChatMessage,
    ChatResponse,
    ChatConfig,
    ChatIntent,
} from './types';
import { classifyChatIntent, requiresConfirmation, isFreeIntent } from './intentClassifier';
import { buildChatPrompt, buildModificationPreview, buildRecipeContext } from './chatPrompts';
import { generateAssistantResponse } from '@/services/ai/aiGateway';
import { getCostForAction } from '@/services/chefcoinService';

export class ChatService {
    private personaId: string;
    private personaChatProfile: string;
    private recipe: RecipeFormData | null;
    private dietaryTags: string[];
    private history: ChatMessage[] = [];
    private isInitialized: boolean = false;
    private defaultModificationCost: number;
    // Cached rendered recipe context. Invalidated in `updateRecipe()`.
    // Identity comparison is enough because `updateRecipe` always receives a
    // new RecipeFormData object after a confirmed modification.
    private cachedRecipeContext: { recipe: RecipeFormData; context: string } | null = null;

    constructor(config: ChatConfig) {
        this.personaId = config.personaId;
        this.personaChatProfile = config.personaChatProfile;
        this.recipe = config.recipe ?? null;
        this.dietaryTags = config.dietaryTags ?? [];
        this.defaultModificationCost = getCostForAction('RECIPE_MODIFY');
    }

    async initialize(): Promise<void> {
        if (this.isInitialized) {
            return;
        }
        this.isInitialized = true;
    }

    getPersonaId(): string {
        return this.personaId;
    }

    getRecipe(): RecipeFormData | null {
        return this.recipe;
    }

    updateRecipe(recipe: RecipeFormData): void {
        this.recipe = recipe;
        this.cachedRecipeContext = null;
    }

    getHistory(): ChatMessage[] {
        return [...this.history];
    }

    clearHistory(): void {
        this.history = [];
    }

    /**
     * Add a history entry from outside the service. Used by the Studio wrapper
     * after a confirmed modification so the user + chef summary show up in
     * the conversation log without re-asking the classifier.
     */
    addHistoryEntry(role: 'user' | 'chef', content: string, intent?: ChatIntent): void {
        this.history.push({
            id: Date.now() + this.history.length,
            role,
            content,
            timestamp: new Date(),
            intent
        });
    }

    isFreeTier(intent: ChatIntent): boolean {
        return isFreeIntent(intent);
    }

    /**
     * Send a message and get a response.
     *
     * Flow:
     * 1. Classify intent (AI-first, uses conversation history for context)
     * 2. If CONFIRM_MODIFY, generate a preview and return with requiresConfirmation=true
     * 3. Otherwise, generate a persona-aware response and add both turns to history
     */
    async sendMessage(userMessage: string): Promise<ChatResponse> {
        if (!this.isInitialized) {
            await this.initialize();
        }

        const trimmedMessage = userMessage.trim();
        if (!trimmedMessage) {
            return { success: false, error: 'Message cannot be empty' };
        }

        try {
            // Step 1: Classify intent WITH conversation history for context
            const historyForClassifier = this.history.map(m => ({
                role: m.role,
                content: m.content
            }));

            const intent = await classifyChatIntent(
                trimmedMessage,
                this.recipe?.title || 'this conversation',
                historyForClassifier
            );


            // Step 2: If CONFIRM_MODIFY, return early with a preview
            if (requiresConfirmation(intent)) {
                // Confirmation-enrichment: if the user's message is a short
                // confirmation (e.g. "ok make the change", "yes do that"),
                // build a richer modification request from the conversation
                // history so the modification handler has real context to
                // work with. The AI intent classifier decides IF this is a
                // confirmation; this helper decides WHAT to actually change.
                const enrichedRequest = enrichConfirmationRequest(trimmedMessage, this.history) ?? trimmedMessage;
                const preview = await this.generateModificationPreview(enrichedRequest);
                return {
                    success: true,
                    intent,
                    requiresConfirmation: true,
                    pendingModification: enrichedRequest,
                    estimatedCost: this.defaultModificationCost,
                    response: preview
                };
            }

            // Step 3: Generate persona-aware response
            const responseText = await this.generateResponse(trimmedMessage, intent);
            if (!responseText) {
                return { success: false, error: 'Failed to generate response' };
            }

            // Step 4: Update conversation history
            this.history.push({
                id: Date.now(),
                role: 'user',
                content: trimmedMessage,
                timestamp: new Date(),
                intent
            });
            this.history.push({
                id: Date.now() + 1,
                role: 'chef',
                content: responseText,
                timestamp: new Date()
            });

            return {
                success: true,
                response: responseText,
                intent
            };
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'An error occurred'
            };
        }
    }

    private getRecipeContext(): string {
        if (!this.recipe) {
            return '(No recipe is currently being discussed)';
        }
        if (this.cachedRecipeContext?.recipe === this.recipe) {
            return this.cachedRecipeContext.context;
        }
        const context = buildRecipeContext(this.recipe);
        this.cachedRecipeContext = { recipe: this.recipe, context };
        return context;
    }

    private async generateResponse(
        userMessage: string,
        intent: ChatIntent
    ): Promise<string | null> {
        const prompt = buildChatPrompt(
            this.personaChatProfile,
            this.recipe,
            userMessage,
            this.history,
            intent,
            this.dietaryTags,
            this.getRecipeContext()
        );

        const result = await generateAssistantResponse(prompt, 'ChatService');

        if (result.success && result.text) {
            return result.text.trim();
        }

        return null;
    }

    private async generateModificationPreview(modificationRequest: string): Promise<string> {
        // If no recipe, fall back to a generic preview (lab assistant can still
        // return CONFIRM_MODIFY if it has a recipe elsewhere; this is just safety)
        if (!this.recipe) {
            return `I'll modify the recipe according to your request: "${modificationRequest}"`;
        }

        const prompt = buildModificationPreview(
            this.personaChatProfile,
            this.recipe,
            modificationRequest
        );

        const result = await generateAssistantResponse(prompt, 'ChatServiceModificationPreview');

        if (result.success && result.text) {
            return result.text.trim();
        }

        return `I'll modify the recipe according to your request: "${modificationRequest}"`;
    }
}

// ============================================================================
// Confirmation enrichment (module-level helpers, not part of the class)
// ============================================================================

/**
 * Phrases that strongly suggest the user is confirming a prior suggestion
 * rather than making a fresh modification request. Used by
 * `enrichConfirmationRequest` to detect "ok do that" / "yes make the change"
 * style messages that need history context to be actionable.
 */
const CONFIRMATION_PHRASES = [
    'ok',
    'yes',
    'sure',
    'do it',
    'go ahead',
    'make the change',
    'sounds good',
    "let's do",
    'lets do',
    'apply that',
    'do that',
    'make it so',
    'please do',
    'do it then',
];

/**
 * Detect short confirmation messages. Conservative: requires BOTH a short
 * length (< 50 chars) AND a confirmation phrase. This avoids false positives
 * like "ok let's add chicken" (too long) or "hello" (no confirmation phrase).
 */
function isShortConfirmation(message: string): boolean {
    const lower = message.toLowerCase().trim();
    if (lower.length >= 50) return false;
    return CONFIRMATION_PHRASES.some(p => lower.includes(p));
}

/**
 * If the user's message is a short confirmation AND there's conversation
 * history, build a richer modification request that includes the prior
 * discussion context. Returns null if no enrichment is possible.
 *
 * Example:
 *   User: "can we swap the tofu for chicken?"
 *   Chef: "Absolutely, swap the tofu for 400g chicken breast..."
 *   User: "ok make the change"
 *   → enrichConfirmationRequest returns:
 *     "Based on the prior discussion: User asked 'can we swap the tofu
 *      for chicken?' You suggested 'Absolutely, swap the tofu for 400g
 *      chicken breast...'. Apply these changes to the recipe."
 */
function enrichConfirmationRequest(
    userMessage: string,
    history: ChatMessage[]
): string | null {
    if (!isShortConfirmation(userMessage)) return null;
    if (history.length === 0) return null;

    const lastChef = [...history].reverse().find(m => m.role === 'chef');
    const lastUser = [...history].reverse().find(m => m.role === 'user');
    if (!lastChef || !lastUser) return null;

    return `Based on the prior discussion:
User asked: "${lastUser.content}"
You suggested: "${lastChef.content}"

Apply these changes to the recipe.`;
}
